import React, { useState, useEffect, useContext, useMemo } from 'react'

import LoadingContext from '../../context/Loading/Loading'

import Button from 'react-bootstrap/Button'
import Modal from 'react-bootstrap/Modal'
import Form from 'react-bootstrap/Form'
import Card from 'react-bootstrap/Card'

import AuthService from '../../utilities/services/auth.service'
import UserContext from '../../context/User/User'
import { ORGANIZATION_PERMISSION_SLUGS } from '@blocktickets/shared-utils'
import { checkPermission } from '../../utilities/helpers'
import {
  getFnbCategories,
  createFnbCategory,
  updateFnbCategory,
  deleteFnbCategory,
  getOrgVenues,
} from '../../utilities/api'

import { LoadingContainer } from '../LoadingContainer'
import { PageLoadingContainer } from '../PageLoadingContainer'
import { NoPermissionsContainer } from '../NoPermissionsContainer'

import '../BuyerTypesWrapper/buyerTypesWrapper.scss'
import '../MarketplaceWrapper/marketplace.scss'

const EMPTY_FORM = {
  name: '',
  code: '',
  venueUuid: '',
  isAlcohol: false,
  isActive: true,
}

function getRowAttrs(row) {
  return row.attributes ?? row
}

function hasMarketplacePermission(getPermissions, orgPermissionsReady) {
  if (!orgPermissionsReady) return false
  const perms = getPermissions()
  return (
    checkPermission(perms, ORGANIZATION_PERMISSION_SLUGS.MANAGE_MARKETPLACE) ||
    checkPermission(perms, ORGANIZATION_PERMISSION_SLUGS.EVENT_MANAGEMENT)
  )
}

export default function MarketplaceCategoriesWrapper() {
  const { isLoading, showLoading, hideLoading } = useContext(LoadingContext)
  const { orgPermissions } = useContext(UserContext)
  const { getPermissions } = AuthService

  const orgUUID = AuthService.getOrg()[0]?.uuid
  const orgPermissionsReady = orgPermissions?.length > 0
  const hasPermission = hasMarketplacePermission(
    getPermissions,
    orgPermissionsReady
  )

  const [categories, setCategories] = useState([])
  const [venues, setVenues] = useState([])
  const [isLoadingVenues, setIsLoadingVenues] = useState(true)
  const [isLoadingData, setIsLoadingData] = useState(true)
  const [showModal, setShowModal] = useState(false)
  const [editingId, setEditingId] = useState(null)
  const [saving, setSaving] = useState(false)
  const [form, setForm] = useState(EMPTY_FORM)

  const requiresVenueSelection = venues.length > 1
  const defaultVenueUuid = venues.length === 1 ? (venues[0]?.uuid ?? '') : ''

  const venueNameByUuid = useMemo(() => {
    const map = new Map()
    venues.forEach((venue) => {
      if (venue?.uuid) {
        map.set(venue.uuid, venue.name ?? venue.uuid)
      }
    })
    return map
  }, [venues])

  const load = () => {
    setIsLoadingData(true)
    getFnbCategories(orgUUID)
      .then((res) => setCategories(res?.data?.data ?? []))
      .catch((err) => console.error(err))
      .finally(() => {
        setIsLoadingData(false)
        hideLoading()
      })
  }

  const loadVenues = () => {
    setIsLoadingVenues(true)
    getOrgVenues(orgUUID)
      .then((res) => {
        setVenues(Array.isArray(res?.data) ? res.data : [])
      })
      .catch((err) => console.error(err))
      .finally(() => setIsLoadingVenues(false))
  }

  useEffect(() => {
    if (!orgPermissionsReady) return
    if (!hasPermission) {
      hideLoading()
      return
    }
    if (!orgUUID) return
    showLoading()
    load()
    loadVenues()
  }, [orgPermissionsReady, hasPermission, orgUUID])

  const openAdd = () => {
    setEditingId(null)
    setForm({
      ...EMPTY_FORM,
      venueUuid: defaultVenueUuid,
    })
    setShowModal(true)
  }

  const openEdit = (row) => {
    const a = getRowAttrs(row)
    setEditingId(row.id)
    setForm({
      name: a.name ?? '',
      code: a.code ?? '',
      venueUuid: a.venue_uuid ?? defaultVenueUuid,
      isAlcohol: a.isAlcohol ?? false,
      isActive: a.isActive !== false,
    })
    setShowModal(true)
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    if (!orgUUID || !form.name.trim()) return
    setSaving(true)
    const venueUuid = form.venueUuid || defaultVenueUuid
    const payload = {
      organization_uuid: orgUUID,
      venue_uuid: venueUuid || null,
      name: form.name.trim(),
      code: form.code.trim(),
      isAlcohol: form.isAlcohol,
      isActive: form.isActive,
    }
    const done = () => {
      setShowModal(false)
      load()
    }
    const req = editingId
      ? updateFnbCategory(editingId, payload)
      : createFnbCategory(payload)
    req
      .then(done)
      .catch((err) => console.error(err))
      .finally(() => setSaving(false))
  }

  const handleDelete = (row) => {
    const a = getRowAttrs(row)
    if (!window.confirm(`Delete category “${a.name}”?`)) return
    deleteFnbCategory(row.id)
      .then(() => load())
      .catch((err) => console.error(err))
  }

  if (!orgPermissionsReady) return <PageLoadingContainer />
  if (!hasPermission) return <NoPermissionsContainer />
  if (isLoading) return <PageLoadingContainer />

  return (
    <>
      <div className="marketplace-layout">
        <div className="marketplace-toolbar">
          <p className="mb-0 text-muted">
            Group items for menus and reporting. Leave venue blank to share a
            category across all venues.
          </p>
          <Button variant="primary" onClick={openAdd}>
            + Add category
          </Button>
        </div>

        {categories.length === 0 && !isLoadingData ? (
          <Card body className="card--sm">
            <div className="marketplace-empty">
              <p className="marketplace-empty__message">No categories yet.</p>
              <Button variant="primary" onClick={openAdd}>
                + Add category
              </Button>
            </div>
          </Card>
        ) : isLoadingData ? (
          <LoadingContainer />
        ) : (
          <div className="buyer-types-table-wrap">
            <table className="buyer-types-table">
              <thead>
                <tr>
                  <th scope="col">Name</th>
                  <th scope="col">Code</th>
                  {requiresVenueSelection ? <th scope="col">Venue</th> : null}
                  <th scope="col">Alcohol</th>
                  <th scope="col">Status</th>
                  <th
                    scope="col"
                    className="buyer-types-table__actions"
                    aria-label="Actions"
                  />
                </tr>
              </thead>
              <tbody>
                {categories.map((row) => {
                  const a = getRowAttrs(row)
                  return (
                    <tr key={row.id}>
                      <td className="buyer-types-table__name">{a.name}</td>
                      <td>{a.code || '—'}</td>
                      {requiresVenueSelection ? (
                        <td>
                          {a.venue_uuid
                            ? (venueNameByUuid.get(a.venue_uuid) ?? '—')
                            : 'All venues'}
                        </td>
                      ) : null}
                      <td>{a.isAlcohol ? 'Yes' : 'No'}</td>
                      <td>
                        <span
                          className={`marketplace-status ${
                            a.isActive
                              ? 'marketplace-status--active'
                              : 'marketplace-status--inactive'
                          }`}
                        >
                          {a.isActive ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="buyer-types-table__actions">
                        <Button
                          variant="link"
                          className="p-0 me-2"
                          onClick={() => openEdit(row)}
                        >
                          Edit
                        </Button>
                        <Button
                          variant="link"
                          className="p-0 text-danger"
                          onClick={() => handleDelete(row)}
                        >
                          Delete
                        </Button>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Modal
        show={showModal}
        onHide={() => !saving && setShowModal(false)}
        centered
      >
        <Form onSubmit={handleSubmit}>
          <Modal.Header closeButton>
            <Modal.Title>
              {editingId ? 'Edit category' : 'Add category'}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form.Group className="mb-3" controlId="cat-name">
              <Form.Label>Name</Form.Label>
              <Form.Control
                value={form.name}
                onChange={(e) =>
                  setForm((f) => ({ ...f, name: e.target.value }))
                }
                required
              />
            </Form.Group>
            <Form.Group className="mb-3" controlId="cat-code">
              <Form.Label>Code</Form.Label>
              <Form.Control
                value={form.code}
                onChange={(e) =>
                  setForm((f) => ({ ...f, code: e.target.value }))
                }
                placeholder="FOOD"
              />
            </Form.Group>
            {requiresVenueSelection ? (
              <Form.Group className="mb-3" controlId="cat-venue">
                <Form.Label>Venue</Form.Label>
                {isLoadingVenues ? (
                  <div className="text-muted small">Loading venues...</div>
                ) : (
                  <Form.Select
                    value={form.venueUuid}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, venueUuid: e.target.value }))
                    }
                  >
                    <option value="">All venues</option>
                    {venues.map((venue) => (
                      <option key={venue.uuid} value={venue.uuid}>
                        {venue.name}
                      </option>
                    ))}
                  </Form.Select>
                )}
                <Form.Text className="text-muted">
                  Optional. Restrict this category to one venue, or leave blank
                  for org-wide use.
                </Form.Text>
              </Form.Group>
            ) : null}
            <Form.Check
              type="switch"
              id="cat-alcohol"
              className="mb-2"
              label="Alcohol category"
              checked={form.isAlcohol}
              onChange={(e) =>
                setForm((f) => ({ ...f, isAlcohol: e.target.checked }))
              }
            />
            <Form.Check
              type="switch"
              id="cat-active"
              label="Active"
              checked={form.isActive}
              onChange={(e) =>
                setForm((f) => ({ ...f, isActive: e.target.checked }))
              }
            />
          </Modal.Body>
          <Modal.Footer>
            <Button
              variant="secondary"
              onClick={() => setShowModal(false)}
              disabled={saving}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="primary"
              disabled={saving || !form.name.trim()}
            >
              {saving ? 'Saving…' : editingId ? 'Save' : 'Add'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </>
  )
}
